import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../services/api";

interface IdentityContact {
  phone: string;
  phone_masked: string;
  name: string;
  short: string;
  role: string;
  emoji: string;
  verified: boolean;
  locked: boolean;
  session_ttl_seconds: number | null;
  has_pin: boolean;
}

// ─── Role hierarchy definition ──────────────────────────────────────────────
const ROLE_HIERARCHY = [
  { role: "super_admin",      label: "Super Admin",      emoji: "⚡", color: "#ffd6a5", level: 10, canView: ["all"] },
  { role: "owner",            label: "Owner",            emoji: "👑", color: "#ffd6a5", level: 9,  canView: ["all"] },
  { role: "general_manager",  label: "General Manager",  emoji: "🏛️", color: "#c8bfff", level: 8,  canView: ["ops", "team", "reports"] },
  { role: "tech_shareholder", label: "Tech Shareholder", emoji: "⚙️", color: "#c8bfff", level: 7,  canView: ["tech", "ops"] },
  { role: "shareholder",      label: "Shareholder",      emoji: "💼", color: "#c8bfff", level: 6,  canView: ["reports", "finance"] },
  { role: "consultant",       label: "Consultant",       emoji: "🤝", color: "#b8d58d", level: 4,  canView: ["reports"] },
  { role: "accountant",       label: "Accountant",       emoji: "📊", color: "#b8d58d", level: 3,  canView: ["finance"] },
  { role: "labor",            label: "Operations",       emoji: "🔧", color: "#b8d58d", level: 2,  canView: ["ops"] },
  { role: "employee",         label: "Employee",         emoji: "👤", color: "#b8d58d", level: 1,  canView: ["self"] },
];

const PERMISSION_MODULES = [
  { id: "dashboard",    label: "Operations Center",  icon: "🏠" },
  { id: "whatsapp",     label: "WhatsApp AI",        icon: "💬" },
  { id: "crm",          label: "CRM",                icon: "🎯" },
  { id: "inventory",    label: "Inventory",          icon: "📦" },
  { id: "attendance",   label: "Attendance",         icon: "⏰" },
  { id: "seo",          label: "SEO Intelligence",   icon: "📡" },
  { id: "shopify",      label: "Shopify / Profit",   icon: "🛍️" },
  { id: "pricing",      label: "Pricing Engine",     icon: "💲" },
  { id: "accounting",   label: "Accounting",         icon: "📚" },
  { id: "hr",           label: "HR & Team",          icon: "👥" },
  { id: "voice-sales",  label: "Voice Sales",        icon: "📞" },
  { id: "leads",        label: "Lead Generation",    icon: "🔍" },
  { id: "integrations", label: "Integrations",       icon: "🔌" },
  { id: "veridian",     label: "Veridian AI",        icon: "✨" },
  { id: "ai",           label: "AI Command Center",  icon: "🤖" },
  { id: "market-intel", label: "Market Intel",       icon: "🔭" },
  { id: "admin",        label: "Admin Panel",        icon: "🛡️" },
];

interface AdminUser {
  id: number;
  name: string;
  phone: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

interface PermMatrix {
  modules: string[];
  users: Array<{
    id: number;
    name: string;
    role: string;
    permissions: Record<string, boolean>;
  }>;
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 18px',
  background: active ? 'rgba(200,191,255,0.15)' : 'transparent',
  border: active ? '1px solid rgba(200,191,255,0.3)' : '1px solid rgba(78,69,59,0.2)',
  borderRadius: 9,
  cursor: 'pointer',
  color: active ? '#c8bfff' : '#6a8090',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: 12, fontWeight: 600,
  transition: 'all 0.18s',
});

const badgeStyle = (color: string): React.CSSProperties => ({
  background: color + '18',
  border: '1px solid ' + color + '35',
  borderRadius: 6,
  padding: '2px 9px',
  fontSize: 10, fontWeight: 700,
  color: color, letterSpacing: '0.05em',
});

const s: Record<string, React.CSSProperties> = {
  page: {
    padding: "24px",
    fontFamily: "'Space Grotesk', sans-serif",
    color: "#dae2fd",
    maxWidth: 1200,
  },
  header: {
    display: "flex", alignItems: "center", gap: 14,
    marginBottom: 28,
  },
  icon: {
    width: 52, height: 52,
    background: "linear-gradient(135deg, rgba(255,107,107,0.2), rgba(255,214,165,0.15))",
    border: "1px solid rgba(255,214,165,0.2)",
    borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 24,
  },
  card: {
    background: "rgba(19,27,46,0.7)",
    backdropFilter: "blur(14px)",
    border: "1px solid rgba(78,69,59,0.2)",
    borderRadius: 14,
    padding: "20px",
    marginBottom: 20,
  },
  roleRow: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid rgba(78,69,59,0.12)",
  },
  table: { width: "100%", borderCollapse: "collapse" as const },
  th: {
    padding: "10px 12px", textAlign: "left" as const,
    fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
    color: "#4a5568", textTransform: "uppercase" as const,
    borderBottom: "1px solid rgba(78,69,59,0.15)",
  },
  td: {
    padding: "10px 12px",
    fontSize: 12.5, color: "#9ab0c9",
    borderBottom: "1px solid rgba(78,69,59,0.08)",
  },
  input: {
    background: "rgba(11,19,38,0.8)",
    border: "1px solid rgba(78,69,59,0.3)",
    borderRadius: 8,
    padding: "7px 12px",
    color: "#dae2fd",
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: 12,
    outline: "none",
  } as React.CSSProperties,
};

const AdminPanel: React.FC = () => {
  const [tab, setTab]       = useState<"roles" | "users" | "permissions" | "identity" | "system">("roles");
  const [identityContacts, setIdentityContacts] = useState<IdentityContact[]>([]);
  const [identityEnabled, setIdentityEnabled] = useState(true);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [users, setUsers]   = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [sysStatus, setSysStatus] = useState<Record<string, any>>({});
  const [matrix, setMatrix] = useState<PermMatrix | null>(null);
  const [matrixLoading, setMatrixLoading] = useState(false);

  // Create-user form state
  const [newName, setNewName]   = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newRole, setNewRole]   = useState("employee");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const loadUsers = useCallback(() => {
    setLoading(true);
    fetch("/api/admin/users", { credentials: "include" })
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === "users") {
      loadUsers();
    }
    if (tab === "permissions") {
      setMatrixLoading(true);
      fetch("/api/admin/permissions/matrix", { credentials: "include" })
        .then(r => r.json())
        .then(d => setMatrix(d))
        .catch(() => {})
        .finally(() => setMatrixLoading(false));
    }
    if (tab === "identity") {
      loadIdentity();
    }
    if (tab === "system") {
      Promise.all([
        fetch("/api/omnivoice/status").then(r => r.json()).catch(() => ({})),
        fetch("/api/system/lockdown-status").then(r => r.json()).catch(() => ({})),
      ]).then(([omni, lockdown]) => {
        setSysStatus({ omnivoice: omni, lockdown });
      });
    }
  }, [tab, loadUsers]);

  const handleCreateUser = async () => {
    if (!newName.trim() || !newPhone.trim()) {
      setCreateError("Name and phone are required.");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const res = await fetch("/api/admin/users", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim(), phone: newPhone.trim(), role: newRole }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setCreateError(e.detail || "Failed to create user.");
      } else {
        setNewName(""); setNewPhone(""); setNewRole("employee");
        loadUsers();
      }
    } catch {
      setCreateError("Network error.");
    } finally {
      setCreating(false);
    }
  };

  const loadIdentity = async () => {
    setIdentityLoading(true);
    try {
      const d = await api.identity.status();
      setIdentityContacts(d.contacts || []);
      setIdentityEnabled(d.enabled !== false);
    } catch {
      setIdentityContacts([]);
    } finally {
      setIdentityLoading(false);
    }
  };

  const handleIdentityReset = async (phone: string) => {
    try {
      await api.identity.reset(phone);
      loadIdentity();
    } catch {}
  };

  const handleIdentityForceVerify = async (phone: string) => {
    try {
      await api.identity.forceVerify(phone);
      loadIdentity();
    } catch {}
  };

  const handleTogglePermission = async (userId: number, moduleId: string, currentValue: boolean) => {
    await fetch(`/api/admin/users/${userId}/permissions`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ module_id: moduleId, granted: !currentValue }),
    });
    fetch("/api/admin/permissions/matrix", { credentials: "include" })
      .then(r => r.json())
      .then(d => setMatrix(d))
      .catch(() => {});
  };

  const TABS: Array<{ id: typeof tab; label: string; icon: string }> = [
    { id: "roles",       label: "Role Hierarchy",  icon: "🏛️" },
    { id: "users",       label: "Users",           icon: "👥" },
    { id: "permissions", label: "Permissions",     icon: "🔐" },
    { id: "identity",    label: "Identity Verify", icon: "🪪" },
    { id: "system",      label: "System Status",   icon: "🖥️" },
  ];

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.icon}>🛡️</div>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, fontFamily: "'Manrope', sans-serif", color: "#ffd6a5" }}>
            Admin Panel
          </h1>
          <p style={{ margin: 0, fontSize: 12, color: "#4a6a7a" }}>
            Role hierarchy · User management · System control
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {TABS.map(t => (
          <button key={t.id} style={tabStyle(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Role Hierarchy ── */}
      {tab === "roles" && (
        <div style={s.card}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, color: "#c8bfff" }}>Access Level Hierarchy</h3>
          <p style={{ fontSize: 12, color: "#4a6070", marginBottom: 20 }}>
            Higher level roles inherit all permissions of lower roles. Board members (Hoda, Soheil, Zoheir, Zeinali, Abdolmadjid) are excluded from KPI tracking.
          </p>
          {[...ROLE_HIERARCHY].reverse().map(r => (
            <div key={r.role} style={s.roleRow}>
              <div style={{
                width: 32, height: 32,
                background: `${r.color}15`,
                border: `1px solid ${r.color}30`,
                borderRadius: 8,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, flexShrink: 0,
              }}>{r.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#dae2fd" }}>{r.label}</div>
                <div style={{ fontSize: 10, color: "#3a4a5a", fontFamily: "monospace" }}>{r.role}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <span style={badgeStyle("#94a3b8")}>Level {r.level}</span>
                {r.canView.map(v => (
                  <span key={v} style={badgeStyle(r.color)}>{v === "all" ? "ALL ACCESS" : v}</span>
                ))}
              </div>
              {/* Level bar */}
              <div style={{ width: 80, height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 2, flexShrink: 0 }}>
                <div style={{
                  height: "100%",
                  width: `${r.level * 10}%`,
                  background: `linear-gradient(90deg, ${r.color}60, ${r.color})`,
                  borderRadius: 2,
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Users ── */}
      {tab === "users" && (
        <div style={s.card}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, color: "#c8bfff" }}>
            All Users {users.length > 0 && <span style={{ color: "#4a6070", fontWeight: 400 }}>({users.length})</span>}
          </h3>

          {/* Create user form */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20, padding: "14px", background: "rgba(200,191,255,0.04)", borderRadius: 10, border: "1px solid rgba(200,191,255,0.1)" }}>
            <input
              style={{ ...s.input, flex: "1 1 140px" }}
              placeholder="Full name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
            />
            <input
              style={{ ...s.input, flex: "1 1 120px" }}
              placeholder="Phone (e.g. 971581234567)"
              value={newPhone}
              onChange={e => setNewPhone(e.target.value)}
            />
            <select
              style={{ ...s.input, flex: "1 1 120px" }}
              value={newRole}
              onChange={e => setNewRole(e.target.value)}
            >
              {ROLE_HIERARCHY.map(r => (
                <option key={r.role} value={r.role}>{r.label}</option>
              ))}
            </select>
            <button
              style={{
                background: creating ? "rgba(200,191,255,0.08)" : "rgba(200,191,255,0.15)",
                border: "1px solid rgba(200,191,255,0.3)",
                borderRadius: 8, padding: "7px 16px",
                cursor: creating ? "not-allowed" : "pointer",
                color: "#c8bfff", fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 12, fontWeight: 600,
              }}
              onClick={handleCreateUser}
              disabled={creating}
            >
              {creating ? "Adding…" : "+ Add User"}
            </button>
            {createError && <div style={{ width: "100%", fontSize: 11, color: "#ff6b6b" }}>{createError}</div>}
          </div>

          {loading ? (
            <div style={{ color: "#4a6070", fontSize: 13 }}>Loading users…</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Phone</th>
                  <th style={s.th}>Role</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const ri = ROLE_HIERARCHY.find(r => r.role === u.role) || ROLE_HIERARCHY[ROLE_HIERARCHY.length - 1];
                  return (
                    <tr key={u.id}>
                      <td style={{ ...s.td, color: "#dae2fd", fontWeight: 600 }}>
                        {ri.emoji} {u.name}
                      </td>
                      <td style={{ ...s.td, fontFamily: "monospace", fontSize: 11 }}>+{u.phone}</td>
                      <td style={s.td}>
                        <span style={badgeStyle(ri.color)}>{ri.label}</span>
                      </td>
                      <td style={s.td}>
                        <span style={{
                          ...badgeStyle(u.is_active ? "#b8d58d" : "#ff6b6b"),
                        }}>{u.is_active ? "Active" : "Inactive"}</span>
                      </td>
                      <td style={{ ...s.td, fontSize: 11 }}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString("en-AE") : "—"}
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr><td colSpan={5} style={{ ...s.td, color: "#2a3a4a", textAlign: "center" }}>No users found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Permissions ── */}
      {tab === "permissions" && (
        <div style={s.card}>
          <h3 style={{ margin: "0 0 6px", fontSize: 14, color: "#c8bfff" }}>Module Access Matrix</h3>
          <p style={{ margin: "0 0 20px", fontSize: 11, color: "#3a5060" }}>
            Toggle checkboxes to grant or revoke module access per user.
          </p>
          {matrixLoading ? (
            <div style={{ color: "#4a6070", fontSize: 13 }}>Loading matrix…</div>
          ) : matrix && matrix.users.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={{ ...s.th, minWidth: 140 }}>Module</th>
                    {matrix.users.map(u => {
                      const ri = ROLE_HIERARCHY.find(r => r.role === u.role) || ROLE_HIERARCHY[ROLE_HIERARCHY.length - 1];
                      return (
                        <th key={u.id} style={{ ...s.th, textAlign: "center" as const, minWidth: 80 }}>
                          {ri.emoji}<br /><span style={{ fontSize: 9 }}>{u.name.split(" ")[0]}</span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {PERMISSION_MODULES.map(mod => (
                    <tr key={mod.id}>
                      <td style={{ ...s.td, color: "#dae2fd" }}>{mod.icon} {mod.label}</td>
                      {matrix.users.map(u => {
                        const granted = u.permissions[mod.id] || false;
                        return (
                          <td key={u.id} style={{ ...s.td, textAlign: "center" as const }}>
                            <input
                              type="checkbox"
                              checked={granted}
                              onChange={() => handleTogglePermission(u.id, mod.id, granted)}
                              style={{ cursor: "pointer", accentColor: "#b8d58d", width: 15, height: 15 }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: "#3a5060", fontSize: 13 }}>
              No users found. Add users in the Users tab first.
            </div>
          )}
        </div>
      )}

      {/* ── Identity Verification ── */}
      {tab === "identity" && (
        <div style={s.card}>
          <h3 style={{ margin: "0 0 6px", fontSize: 14, color: "#c8bfff" }}>
            WhatsApp Identity Verification
          </h3>
          <p style={{ margin: "0 0 16px", fontSize: 11, color: "#3a5060" }}>
            Known contacts must complete a PIN challenge on first WhatsApp message.
            Session lasts 24h. Three failed attempts trigger a 1-hour lockout.
            Status: <span style={badgeStyle(identityEnabled ? "#b8d58d" : "#ff9f7f")}>
              {identityEnabled ? "ENABLED" : "DISABLED"}
            </span>
          </p>
          {identityLoading ? (
            <div style={{ color: "#4a6070", fontSize: 13 }}>Loading…</div>
          ) : identityContacts.length === 0 ? (
            <div style={{ color: "#3a5060", fontSize: 13 }}>No known contacts found.</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Contact</th>
                  <th style={s.th}>Phone</th>
                  <th style={s.th}>Role</th>
                  <th style={s.th}>PIN</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Session</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {identityContacts.map(c => {
                  const status = c.verified
                    ? { label: "✅ Verified", color: "#b8d58d" }
                    : c.locked
                      ? { label: "🔒 Locked", color: "#ff6b6b" }
                      : { label: "⬜ Not verified", color: "#94a3b8" };
                  const ttlLabel = c.session_ttl_seconds
                    ? `${Math.floor(c.session_ttl_seconds / 3600)}h ${Math.floor((c.session_ttl_seconds % 3600) / 60)}m`
                    : "—";
                  return (
                    <tr key={c.phone}>
                      <td style={{ ...s.td, color: "#dae2fd", fontWeight: 600 }}>
                        {c.emoji} {c.short}
                      </td>
                      <td style={{ ...s.td, fontFamily: "monospace", fontSize: 11 }}>
                        +{c.phone_masked}
                      </td>
                      <td style={s.td}>
                        <span style={badgeStyle("#c8bfff")}>{c.role}</span>
                      </td>
                      <td style={s.td}>
                        <span style={badgeStyle(c.has_pin ? "#b8d58d" : "#ff9f7f")}>
                          {c.has_pin ? "set" : "unset"}
                        </span>
                      </td>
                      <td style={s.td}>
                        <span style={badgeStyle(status.color)}>{status.label}</span>
                      </td>
                      <td style={{ ...s.td, fontSize: 11 }}>{ttlLabel}</td>
                      <td style={s.td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => handleIdentityReset(c.phone)}
                            style={{
                              background: "rgba(255,107,107,0.1)",
                              border: "1px solid rgba(255,107,107,0.3)",
                              borderRadius: 6, padding: "4px 9px", cursor: "pointer",
                              color: "#ff9f7f", fontSize: 10, fontWeight: 600,
                              fontFamily: "'Space Grotesk', sans-serif",
                            }}
                          >Reset</button>
                          <button
                            onClick={() => handleIdentityForceVerify(c.phone)}
                            style={{
                              background: "rgba(184,213,141,0.1)",
                              border: "1px solid rgba(184,213,141,0.3)",
                              borderRadius: 6, padding: "4px 9px", cursor: "pointer",
                              color: "#b8d58d", fontSize: 10, fontWeight: 600,
                              fontFamily: "'Space Grotesk', sans-serif",
                            }}
                          >Force Verify</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p style={{ marginTop: 14, fontSize: 10, color: "#3a5060" }}>
            PINs are stored in .env as <code>VERIFY_PIN_{"{phone}"}</code>. Reach out to super admin to set or rotate PINs.
          </p>
        </div>
      )}

      {/* ── System Status ── */}
      {tab === "system" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { label: "Backend API",     icon: "⚙️",  status: "Operational",  color: "#b8d58d" },
              { label: "PostgreSQL DB",   icon: "🗄️",  status: "Healthy",      color: "#b8d58d" },
              { label: "Redis Cache",     icon: "⚡",  status: "Healthy",      color: "#b8d58d" },
              { label: "Nginx Proxy",     icon: "🌐",  status: "Operational",  color: "#b8d58d" },
              { label: "WhatsApp (UltraMsg)", icon: "💬", status: "Connected",  color: "#b8d58d" },
              { label: "ElevenLabs TTS",  icon: "🎙️",  status: "Active",       color: "#b8d58d" },
              { label: "OmniVoice",       icon: "🎛️",  status: sysStatus.omnivoice?.ffmpeg ? "Ready" : "ffmpeg missing", color: sysStatus.omnivoice?.ffmpeg ? "#b8d58d" : "#ff9f7f" },
              { label: "System Lockdown", icon: "🔒",  status: sysStatus.lockdown?.locked ? "LOCKED" : "Unlocked", color: sysStatus.lockdown?.locked ? "#ff6b6b" : "#b8d58d" },
            ].map(item => (
              <div key={item.label} style={{ ...s.card, marginBottom: 0, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ fontSize: 24 }}>{item.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#dae2fd" }}>{item.label}</div>
                </div>
                <span style={badgeStyle(item.color)}>{item.status}</span>
              </div>
            ))}
          </div>

          <div style={{ ...s.card, marginTop: 16 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 13, color: "#c8bfff" }}>Quick Actions</h3>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[
                { label: "Restart Backend",  action: "backend",  icon: "🔄", color: "#ffd6a5" },
                { label: "Clear Redis Cache", action: "redis",    icon: "🗑️", color: "#ff9f7f" },
                { label: "View Backend Logs", action: "logs",     icon: "📋", color: "#c8bfff" },
              ].map(btn => (
                <button
                  key={btn.action}
                  style={{
                    background: `${btn.color}10`,
                    border: `1px solid ${btn.color}30`,
                    borderRadius: 9,
                    padding: "8px 16px",
                    cursor: "pointer",
                    color: btn.color,
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 12, fontWeight: 600,
                  }}
                  onClick={() => alert(`${btn.label} — run manually via Docker: docker restart afaq-os-v100-backend-1`)}
                >
                  {btn.icon} {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;